// Settings Service - Handles user preferences and settings
// Currency, timezone, language, and other user preferences

import type { TradeTablePreferences } from '@bat/shared'
import { apiService } from '@platform/api/api.service'

import { ApiException } from '@/shared/utils/api'
import { logger } from '@/shared/utils/logger'

interface UserPreferencesResponse {
  preferences: Record<string, unknown>
  reportingCurrency?: string
  message?: string
}

interface UpdatePreferencesRequest {
  baseCurrency?: string
  timezone?: string
  language?: string
  theme?: string
  dateFormat?: string
  numberFormat?: string
  tradeTable?: TradeTablePreferences
  // Per-user override for the access-token TTL (minutes). Backend whitelist:
  // 30, 60, 120, 480, 1440, 10080. Pass `null` to reset to role-based default.
  sessionTtlMinutes?: number | null
  [key: string]: unknown
}

class SettingsService {
  private readonly preferencesEndpoint = '/api/user/preferences'

  /**
   * Get current user preferences
   */
  async getPreferences(): Promise<UserPreferencesResponse> {
    try {
      const response = await apiService.get<UserPreferencesResponse>(this.preferencesEndpoint)

      if (response.success && response.data) {
        return response.data
      }

      throw new ApiException({
        status: 0,
        code: 'FETCH_PREFERENCES_FAILED',
        message: response.message ?? '',
      })
    } catch (error) {
      // Demote benign mid-flight aborts (timeouts, network blips, request
      // cancellations on route change) to a debug log to keep the console
      // clean. Real 4xx/5xx still surfaces at error level.
      const code =
        (error as { code?: string } | null)?.code ??
        (error as { error?: { code?: string } } | null)?.error?.code
      if (code === 'TIMEOUT' || code === 'NETWORK_ERROR' || code === 'REQUEST_ABORTED') {
        logger.debug('Get preferences aborted/timed out:', error)
      } else {
        logger.error('Get preferences error:', error)
      }
      throw error
    }
  }

  /**
   * Update user preferences
   * @param preferences - Preferences to update
   */
  async updatePreferences(preferences: UpdatePreferencesRequest): Promise<UserPreferencesResponse> {
    try {
      const response = await apiService.put<UserPreferencesResponse>(
        this.preferencesEndpoint,
        preferences,
      )

      if (response.success && response.data) {
        return response.data
      }

      throw new ApiException({
        status: 0,
        code: 'UPDATE_PREFERENCES_FAILED',
        message: response.message ?? '',
      })
    } catch (error) {
      logger.error('Update preferences error:', error)
      throw error
    }
  }

  /**
   * Update only the base currency preference
   * This syncs with the reportingCurrency column in the database
   * @param currency - Currency code (USD, EUR, GBP, JPY, etc.)
   */
  async updateCurrency(currency: string): Promise<UserPreferencesResponse> {
    return this.updatePreferences({ baseCurrency: currency })
  }

  /**
   * Get current user's reporting currency
   */
  async getCurrency(): Promise<string> {
    try {
      const prefs = await this.getPreferences()
      return (prefs.reportingCurrency as string) ?? 'EUR'
    } catch (error) {
      logger.warn('Failed to get currency, using default:', error)
      return 'EUR'
    }
  }
}

// Singleton instance
export const settingsService = new SettingsService()
